/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef, useCallback } from 'preact/hooks';

/*
  threshold: Minimal threshold required before recognizing
  tolerance: A tolerance value which allows the user to move their finger about a length measured in pixels.
  This allows the swipe gesture to be triggered more easily since a User might not move their finger
  in a straight line.
*/
type SwipeOptions = {
  threshold?: number;
  maximumTime?: number;
  tolerance?: number;
  isDisabled?: boolean;
};

type SwipeInfo = {
  direction: 'down';
};

type SwipeState = {
  pointerDownId: number | null;
  startTime: number | null;
  prevpageX: number;
  prevpageY: number;
  elementScrollTop: number;
};
/*This "private" hook will live until we end up stating a final decision on useSwipe hook.
For that hook to work, element that use it has to set touch-action:none;, since pointer events
are canceled once a touch action is going to be handled by the browser. This works for most
of the components that don't scroll. But if the component scroll, there is a need for the browser to 
handle scroll which is imposible with a hook just using pointer events.

Update 11/6/23:
Nishad's team is going to confirm if other comps that will use useSwipe need mouse interactions. If they need it,
we can either have a flag to use pointer or touch events or two separated hooks. If they don't need mouse interactions,
we could start using useSwipe with touch events instead.
*/
export function useSheetSwipe(
  onSwipe: (e: SwipeInfo) => void,
  { threshold = 10, maximumTime = 300, tolerance = 50, isDisabled = false }: SwipeOptions = {
    threshold: 10,
    maximumTime: 300,
    tolerance: 50,
    isDisabled: false
  }
): { swipeProps: Record<string, any> } {
  const ref = useRef<SwipeState>({
    pointerDownId: 0,
    startTime: null,
    prevpageX: 0,
    prevpageY: 0,
    elementScrollTop: 0
  });

  const clearSwipe = useCallback(() => {
    ref.current = {
      pointerDownId: null,
      startTime: 0,
      prevpageX: 0,
      prevpageY: 0,
      elementScrollTop: 0
    };
  }, []);

  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      const touchobj = e.changedTouches[0];
      if (!ref.current.pointerDownId) {
        ref.current = {
          pointerDownId: touchobj.identifier,
          startTime: e.timeStamp,
          prevpageX: touchobj.pageX,
          prevpageY: touchobj.pageY,
          elementScrollTop: (e.currentTarget as HTMLElement).scrollTop
        };
      } else {
        clearSwipe();
      }
    },
    [clearSwipe]
  );

  const onTouchEnd = useCallback(
    (ev: TouchEvent) => {
      const touchobj = ev.changedTouches[0];
      if (touchobj.identifier === ref.current.pointerDownId) {
        if (ref.current.startTime && ev.timeStamp - ref.current.startTime < maximumTime) {
          if (
            touchobj.pageY - ref.current.prevpageY > threshold &&
            Math.abs(touchobj.pageX - ref.current.prevpageX) <= tolerance &&
            ref.current.elementScrollTop === 0
          ) {
            onSwipe?.({ direction: 'down' });
          }
        }
      }
      // state would be reset to its original state whether swipe is completed or cancelled
      clearSwipe();
    },
    [threshold, onSwipe, clearSwipe, maximumTime, tolerance]
  );

  const swipeProps = isDisabled
    ? {}
    : {
        /** event handlers to spread on the target element. */
        onTouchStart,
        onTouchEnd,
        ontouchcancel: clearSwipe
      };

  return {
    swipeProps
  };
}
